import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface DownloadOptions {
  /** Default filename if Content-Disposition header is not present */
  defaultFilename: string;
  /** MIME type for the blob */
  mimeType: string;
  /** If true, opens in new tab instead of downloading */
  openInNewTab?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  private http = inject(HttpClient);

  /**
   * Download a file from a URL and trigger browser download or open in new tab.
   * Extracts filename from Content-Disposition header if available.
   */
  async downloadFile(url: string, options: DownloadOptions): Promise<void> {
    const response = await firstValueFrom(
      this.http.get(url, {
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const filename = this.extractFilename(response, options.defaultFilename);
    this.triggerDownload(
      response.body!,
      options.mimeType,
      filename,
      options.openInNewTab,
    );
  }

  downloadText(body: string, options: DownloadOptions): void {
    this.triggerDownload(
      new Blob([body]),
      options.mimeType,
      options.defaultFilename,
      options.openInNewTab,
    );
  }

  /**
   * Extract filename from Content-Disposition header or use default.
   */
  private extractFilename(
    response: HttpResponse<Blob>,
    defaultFilename: string,
  ): string {
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
    return filenameMatch ? filenameMatch[1] : defaultFilename;
  }

  /**
   * Create a blob URL and trigger download or open in new tab.
   */
  private triggerDownload(
    body: Blob,
    mimeType: string,
    filename: string,
    openInNewTab?: boolean,
  ): void {
    const blob = new Blob([body], { type: mimeType });
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;

    if (openInNewTab) {
      link.target = '_blank';
    } else {
      link.download = filename;
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL after a delay to ensure the download has been triggered
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
  }
}
